import '../scss/components/_addroommodal.scss';
import React, { useState, useEffect } from 'react';
import { roommaster_Insert, getRoomTypes } from '../apis/services/pgApi';   // ← Import getRoomTypes

// ─── Floor Options ───────────────────────────────────────────────
const floorOptions = [
  { value: "Ground Floor", label: "Ground Floor" },
  { value: "1st Floor", label: "1st Floor" },
  { value: "2nd Floor", label: "2nd Floor" },
  { value: "3rd Floor", label: "3rd Floor" },
  { value: "4th Floor", label: "4th Floor" },
];

// ─── Status Options ──────────────────────────────────────────────
const statusOptions = [
  { value: "AVAILABLE", label: "Available" },
  { value: "FULL", label: "Full" },
  { value: "MAINTENANCE", label: "Maintenance" },
];

// ─── Amenities List ──────────────────────────────────────────────
const amenitiesList = ["AC", "WiFi", "Attached Bath", "Common Bath", "TV", "Fan", "Balcony", "Refrigerator"];

// ─── AddRoomModal Component ──────────────────────────────────────
const AddRoomModal = ({ isOpen, onClose, selectedPG, onRoomAdded }) => {
  const [form, setForm] = useState({
    roomNumber: '',
    floor: '',
    roomType: '',
    totalBeds: '',
    status: 'AVAILABLE',
    amenities: [],
    roomSize: '',
    description: '',
    roomPhotos: null
  });

  const [roomTypes, setRoomTypes] = useState([]);     // ← New state for API data
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Fetch Room Types from API
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const data = await getRoomTypes();
        setRoomTypes(data || []);
      } catch (err) {
        console.error('Failed to fetch room types:', err);
        setApiError('Failed to load room types. Please try again.');
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchRoomTypes();
  }, []);

  // Auto-fill Total Beds when Room Type is selected
  useEffect(() => {
    if (form.roomType && roomTypes.length > 0) {
      const selected = roomTypes.find(rt => rt.PhotoType === form.roomType);
      if (selected) {
        set('totalBeds', selected.Capacity.toString());
      }
    }
  }, [form.roomType, roomTypes]);

  if (!isOpen) return null;

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const toggleAmenity = (amenity) => {
    setForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const reset = () => {
    setForm({
      roomNumber: '',
      floor: '',
      roomType: '',
      totalBeds: '',
      status: 'AVAILABLE',
      amenities: [],
      roomSize: '',
      description: '',
      roomPhotos: null
    });
    setErrors({});
    setApiError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = () => {
    const e = {};
    if (!form.roomNumber.trim()) e.roomNumber = 'Room number is required';
    if (!form.floor) e.floor = 'Please select a floor';
    if (!form.roomType) e.roomType = 'Please select room type';
    if (!form.totalBeds) e.totalBeds = 'Number of beds is required';

    const bedsNum = parseInt(form.totalBeds);
    if (bedsNum < 1 || bedsNum > 10) {
      e.totalBeds = 'Beds must be between 1 and 10';
    }
    return e;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    if (!selectedPG || !selectedPG.PGID) {
      setApiError('No PG selected. Please select a PG from the dashboard.');
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      const userId = localStorage.getItem('userId');

      const roomData = {
        PGID: selectedPG.PGID,
        Floor: form.floor,
        RoomNumber: form.roomNumber,
        RoomType: form.roomType,
        Capacity: parseInt(form.totalBeds),
        Status: form.status,
      };

      let response;
      if (form.roomPhotos && form.roomPhotos.length > 0) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(roomData));
        for (let i = 0; i < form.roomPhotos.length; i++) {
          formData.append('photos', form.roomPhotos[i]);
        }
        response = await roommaster_Insert(formData);
      } else {
        response = await roommaster_Insert(roomData);
      }

      if (response && (response.Success === 1 || response.statusCode === 201 || response.Message?.includes('success'))) {
        if (onRoomAdded) {
          onRoomAdded({
            ...form,
            id: response.RoomID || response.data?.roomId,
            submittedAt: new Date().toISOString()
          });
        }

        alert(response.Message || 'Room added successfully!');
        reset();
        onClose();
      } else {
        throw new Error(response?.Message || 'Failed to add room');
      }
    } catch (error) {
      console.error('Error adding room:', error);
      setApiError(error.message || 'Failed to add room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    set('roomPhotos', files);
  };

  return (
    <div className="arm-overlay" >
      <div className="arm-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="arm-head">
          <div>
            <h2 className="arm-title">Add New Room</h2>
            {selectedPG && (
              <p className="arm-sub">
                PG: <strong>{selectedPG.PGName}</strong>
                {selectedPG.PGCode && <span className="arm-pg-code"> ({selectedPG.PGCode})</span>}
              </p>
            )}
          </div>
          <button className="arm-close" onClick={handleClose}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* API Error */}
        {apiError && (
          <div className="arm-error-box">
            <strong>Error:</strong> {apiError}
          </div>
        )}

        {/* Form Body */}
        <div className="arm-form-body">
          <div className="arm-section">
            <h4 className="arm-section-title">Basic Information</h4>
            <div className="arm-grid">
              
              <div className={`arm-field ${errors.roomNumber ? 'arm-field-err' : ''}`}>
                <label className="arm-label">Room Number <span className="arm-req">*</span></label>
                <input 
                  className="arm-input" 
                  placeholder="e.g. 101" 
                  value={form.roomNumber} 
                  onChange={e => set('roomNumber', e.target.value)} 
                />
                {errors.roomNumber && <span className="arm-err-msg">{errors.roomNumber}</span>}
              </div>
              
              <div className={`arm-field ${errors.floor ? 'arm-field-err' : ''}`}>
                <label className="arm-label">Floor <span className="arm-req">*</span></label>
                <select 
                  className="arm-select" 
                  value={form.floor} 
                  onChange={e => set('floor', e.target.value)}
                >
                  <option value="">Select floor</option>
                  {floorOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.floor && <span className="arm-err-msg">{errors.floor}</span>}
              </div>
              
              {/* Room Type Dropdown from API */}
              <div className={`arm-field ${errors.roomType ? 'arm-field-err' : ''}`}>
                <label className="arm-label">Room Type <span className="arm-req">*</span></label>
                <select 
                  className="arm-select" 
                  value={form.roomType} 
                  onChange={e => set('roomType', e.target.value)}
                  disabled={loadingTypes}
                >
                  <option value="">Select type</option>
                  {roomTypes.map((rt, index) => (
                    <option key={index} value={rt.PhotoType}>
                      {rt.PhotoType} ({rt.Capacity} Bed{rt.Capacity > 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
                {errors.roomType && <span className="arm-err-msg">{errors.roomType}</span>}
              </div>
              
              {/* Total Beds - Auto Filled */}
              <div className={`arm-field ${errors.totalBeds ? 'arm-field-err' : ''}`}>
                <label className="arm-label">Total Beds <span className="arm-req">*</span></label>
                <input 
                  className="arm-input" 
                  type="number" 
                  min="1" 
                  max="10"
                  placeholder="Auto-filled from room type"
                  value={form.totalBeds} 
                  onChange={e => set('totalBeds', e.target.value)} 
                  readOnly   // Recommended: prevent manual edit
                />
                {errors.totalBeds && <span className="arm-err-msg">{errors.totalBeds}</span>}
              </div>
              
              <div className="arm-field">
                <label className="arm-label">Status</label>
                <select 
                  className="arm-select" 
                  value={form.status} 
                  onChange={e => set('status', e.target.value)}
                  disabled={true}
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="arm-footer">
          <button className="arm-btn arm-btn-outline" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className="arm-btn arm-btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <span className="arm-spinner"></span>
                Adding Room...
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Add Room
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        .arm-spinner {
          display: inline-block;
          width: 12px;
          height: 12px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: arm-spin 0.6s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes arm-spin {
          to { transform: rotate(360deg); }
        }
        
        .arm-error-box {
          margin: 16px 24px;
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FEE2E2;
          border-radius: 8px;
          color: #DC2626;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
};

export default AddRoomModal;
